import '@widesk-ui/styles/style.scss';
import '@/styles/style.scss';

import 'dayjs/locale/fr';

import AuthProvider                         from '@widesk-ui/contexts/AuthContext';
import ConfigProvider                       from 'antd/lib/config-provider';
import LoginCheckModel                      from '@/models/services/fondations/LoginCheckModel';
import React                                from 'react';
import RouterProvider                       from '@/RouterProvider';
import ThemeModeContext                     from '@widesk-ui/contexts/ThemeModeContext';
import { ThemeMode }                        from '@widesk-ui/contexts/ThemeModeContext';
import appStore                             from '@/stores/appStore';
import authStore                            from '@widesk-core/stores/authStore';
import dayjs                                from 'dayjs';
import errorsFr                             from '@/errorTranslations/fr.json';
import listFilterManagerStore               from '@widesk-ui/stores/ListFilterManagerStore';
import locale                               from 'antd/locale/fr_FR';
import localizedFormat                      from 'dayjs/plugin/localizedFormat';
import advancedFormat                       from 'dayjs/plugin/advancedFormat';
import theme                                from 'antd/lib/theme';
import utc                                  from 'dayjs/plugin/utc';
import { setErrorsTranslations }            from '@widesk-core/tools/getTextAndTranslateFromError';
import ApiModel                             from '@widesk-core/models/ApiModel';
import LocaleCollection                     from '@widesk-core/models/LocaleCollection';
import cacheSystem                          from '@widesk-core/models/apiCacheSystem';
import axios                                from 'axios';
import { getUserRightsFromTokenData }       from '@/helpers/JwtTokenHelper';
import { getRoleIdentifiersFromUserRights } from '@/helpers/JwtTokenHelper';

dayjs.locale('fr');
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

setErrorsTranslations(errorsFr);

// Pour utiliser LocaleCollection par défault à la place de ApiCollection,
// Ce qui permettra aux traductions d'être fetch par défault pour les models compatibles (lang=getLocale())
ApiModel.getResolvableCollection = () => LocaleCollection;

const LOCALSTORAGE_THEME_KEY = 'theme_mode';

const allowedRoleIdentifiers = ['ROLE_WEBSITE_ADMIN'];

const logout = async () => {
	cacheSystem.clear();
	listFilterManagerStore.clear();
	appStore.user.clear();
	authStore.clear();
};

export default function App() {
	const [mode, setMode] = React.useState<ThemeMode>(
		localStorage.getItem(LOCALSTORAGE_THEME_KEY) as ThemeMode || 'light',
	);

	// On sauvegarde le mode en localStorage quand il change
	React.useEffect(() => localStorage.setItem(LOCALSTORAGE_THEME_KEY, mode), [mode]);

	return (
		<ThemeModeContext.Provider value={{ mode, setMode }}>
			<ConfigProvider
				locale={locale}
				theme={{
					algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
					token: {
						colorPrimary: '#2563EB',
						Pagination: {
							showPrevNextJumpers: true,
						},
					} as any,
				}}
			>
				<AuthProvider
					onLogin={async (_username, _password) => {
						const loginCheckModel = new LoginCheckModel();
						await loginCheckModel.patch({ _password, _username, from: 1 }, { stringifyAttributes: true });

						if (loginCheckModel.roleIdentifiers.every(identifier => !allowedRoleIdentifiers.includes(identifier))) {
							throw new Error(`Vous n'êtes pas autorisé à vous connecter sur cette interface.`);
						}

						return [loginCheckModel.token, loginCheckModel.refreshToken];
					}}
					onLogout={logout}
					onLogged={async () => { // L'application charge tant que cette méthode n'est pas terminée
						const protoUserRights = getUserRightsFromTokenData(authStore.tokenData);
						const roleIdentifiers = getRoleIdentifiersFromUserRights(protoUserRights);

						if (roleIdentifiers.every(identifier => !allowedRoleIdentifiers.includes(identifier))) {
							return logout();
						}

						authStore.setRefreshTokenRequest(async () => {
							const service = window.APP_CONFIG.services.find(service => service.id === 'fondations');

							const response = await axios.post(service?.configurations.api_endpoint + '/token/refresh', {
								refresh_token: authStore.refreshToken,
								from: 1,
							});

							return { token: response.data.token, refreshToken: response.data.refresh_token };
						});

						const userId = protoUserRights.urn?.identifier;

						if (userId) {
							await appStore.user.setId(userId).fetch();
						}

						await appStore.load();
					}}
				>
					<RouterProvider />
				</AuthProvider>
			</ConfigProvider>
		</ThemeModeContext.Provider>
	);
}
